import React from 'react';
import { getAuthorizationService } from '../../services/authorizationService';
import { authService } from '../../auth/authService';
import SelkirkIcon from '../icons/SelkirkIcon';
import { ICON } from '../../icons/iconIds';
import './Sidebar.css';

const Sidebar = ({ 
  collapsed = false, 
  currentRoute = '/',
  onNavigate 
}) => {
  const authorizationService = getAuthorizationService(authService);
  
  // Get accessible applications
  const accessibleApps = authorizationService.getAccessibleApps();
  
  // Navigation items
  const navigationItems = [
    {
      id: 'home',
      path: '/',
      iconId: ICON.navHome,
      label: 'Home',
      description: 'Application launcher'
    },
    ...accessibleApps.map(app => ({
      id: app.id,
      path: app.path,
      iconId: app.iconId,
      label: app.name,
      description: app.description,
      status: app.status
    }))
  ];

  // Admin section (if user has admin access)
  const adminItems = authorizationService.isPlatformAdmin() ? [
    {
      id: 'admin',
      path: '/admin',
      iconId: ICON.appAdministration,
      label: 'Administration',
      description: 'Platform administration'
    }
  ] : [];

  const handleNavigation = (path) => {
    if (onNavigate) {
      onNavigate(path);
    } else {
      window.location.hash = path;
    }
  };

  const isActive = (path) => {
    if (path === '/') {
      return currentRoute === '/' || currentRoute === '';
    }
    return currentRoute === path;
  };

  const getStatusIndicator = (status) => {
    if (!status || status === 'active') return null;
    
    const statusConfig = {
      development: { color: '#f59e0b', text: 'Dev' },
      planned: { color: '#6b7280', text: 'Soon' }
    };
    
    const config = statusConfig[status];
    if (!config) return null;
    
    return (
      <span 
        className="status-indicator"
        style={{ backgroundColor: config.color }}
        title={`Status: ${status}`}
      >
        {collapsed ? '•' : config.text}
      </span>
    );
  };

  return (
    <aside className={`sidebar ${collapsed ? 'collapsed' : 'expanded'}`}>
      <div className="sidebar-content">
        {/* Main Navigation */}
        <nav className="sidebar-nav">
          <div className="nav-section">
            {!collapsed && <h3 className="nav-section-title">Navigation</h3>}
            <ul className="nav-list">
              {navigationItems.map((item) => (
                <li key={item.id} className="nav-item">
                  <button
                    className={`nav-link ${isActive(item.path) ? 'active' : ''} ${item.status === 'planned' ? 'disabled' : ''}`}
                    onClick={() => item.status !== 'planned' && handleNavigation(item.path)}
                    title={collapsed ? `${item.label} - ${item.description}` : item.description}
                    disabled={item.status === 'planned'}
                  >
                    <span className="nav-icon">
                      <SelkirkIcon name={item.iconId} variant="nav" decorative />
                    </span>
                    {!collapsed && (
                      <div className="nav-text">
                        <span className="nav-label">{item.label}</span>
                        {getStatusIndicator(item.status)}
                      </div>
                    )}
                    {collapsed && getStatusIndicator(item.status)}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Admin Section */}
          {adminItems.length > 0 && (
            <div className="nav-section">
              {!collapsed && <h3 className="nav-section-title">Administration</h3>}
              <ul className="nav-list">
                {adminItems.map((item) => (
                  <li key={item.id} className="nav-item">
                    <button
                      className={`nav-link ${isActive(item.path) ? 'active' : ''}`}
                      onClick={() => handleNavigation(item.path)}
                      title={collapsed ? `${item.label} - ${item.description}` : item.description}
                    >
                      <span className="nav-icon">
                      <SelkirkIcon name={item.iconId} variant="nav" decorative />
                    </span>
                      {!collapsed && (
                        <div className="nav-text">
                          <span className="nav-label">{item.label}</span>
                        </div>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </nav>

        {/* Sidebar Footer */}
        <div className="sidebar-footer">
          {!collapsed && (
            <div className="footer-content">
              <div className="footer-info">
                <p className="footer-title">Selkirk Copper</p>
                <p className="footer-subtitle">Data Platform v1.0</p>
              </div>
            </div>
          )}
          
          {/* Quick Actions */}
          <div className="quick-actions">
            <button 
              className="quick-action-btn"
              title="Help & Documentation"
              onClick={() => {/* Open help */}}
            >
              <span className="action-icon">
                <SelkirkIcon name={ICON.navHelp} variant="nav" decorative />
              </span>
              {!collapsed && <span className="action-label">Help</span>}
            </button>
            
            <button 
              className="quick-action-btn"
              title="Settings"
              onClick={() => {/* Open settings */}}
            >
              <span className="action-icon">
                <SelkirkIcon name={ICON.navSettings} variant="nav" decorative />
              </span>
              {!collapsed && <span className="action-label">Settings</span>}
            </button>
          </div>
        </div>
      </div>

      {/* Resize Handle */}
      <div className="sidebar-resize-handle"></div>
    </aside>
  );
};

export default Sidebar;
